import type {LATNode as LATNodeType, QueueRoutingRule} from 'src/types/chatbot';
import type {NodeEditorProps} from './node-editor-v2';
import type {LATQueueEntry} from 'src/types/live-agent-transfer';

import produce from 'immer';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {Link} from 'src/rerouter';
import {useApi} from 'src/hooks/useApi';
import {useMutableFieldStatus} from '../api-hooks';
import {hasText} from 'src/utils/draft';
import {useReleaseFlag} from 'src/hooks/product-flags';

import {useChatbotStateContext} from '../chatbot-state-context';
import {
  useAllVariables,
  RuleEditor,
  PRED_TYPE_LABEL_MAP,
  keyForPredicate,
} from './lat-queue-rule-editor';

import {
  findAncestorNodeVariables,
  createLATNode,
  createEditableGraph,
  getHasAncestorJobMatchQuestionNode,
  getIsUsingDiscoverAttribute,
} from '../utils';

import {Button} from 'src/designSystem2021Components/buttons';
import {Input} from 'src/designSystem2021Components/input';

import {Button as GenesisButton} from '@spaced-out/ui-design-system/lib/components/Button';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {TabList, Tab} from '@spaced-out/ui-design-system/lib/components/Tabs';
import {
  SubTitleLarge,
  SubTitleSmall,
  SubTitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';

import {
  MessageDraftEditor,
  upgradePossibleEntitiesInRDCS,
} from 'src/components/conversation-builder/draft-editor';

import {Dropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {Card} from '@spaced-out/ui-design-system/lib/components/Card';

import {useEditorState, useChatbotVariables} from '../node-editor/reducer';

import css from './node-editor-v2.css';
import nodeCss from './lat.css';
import {useSelector} from 'react-redux';
import {selectEntityTypeMappingsByName} from 'src/selectors/ats-entities';
import RichEditorWithWarning from 'src/components/conversation-builder/lib/rich-editor-with-warning/rich-editor-with-warning';
import {getDiscoverAttributeWarningMessage} from 'src/components/conversation-builder/constants';
import {RawDraftContentState, DraftDecoratorType} from 'draft-js';


function validateNode(node: LATNodeType): boolean {
  return (
    node.additional_configs_json.queue_routing_rules.default_queue_id != null &&
    hasText(node.additional_configs_json.response_templates.agent_joined) &&
    hasText(node.additional_configs_json.response_templates.after_hours) &&
    node.additional_configs_json.queue_routing_rules.rules.every(
      (rule) => rule.queue_id != null && rule.queue_id !== '',
    )
  );
}

export function LATNode({
  graph,
  onCancel,
  readOnly,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const richTextEditorEnabled = useReleaseFlag('chatbotFlowRichEditor');
  const richTextEditorFormattingEnabled = useReleaseFlag(
    'chatbotFlowRichEditorFormatting',
  );
  const chatbotNewHyperlinkModalEnabled = useReleaseFlag(
    'chatbotNewHyperlinkModal',
  );
  const atsMappings = useSelector(selectEntityTypeMappingsByName);
  const allowMeetingNodeVariables = Boolean(
    useReleaseFlag('chatbotMeetingNodeVariable'),
  );

  const {state, dispatch} = useChatbotStateContext();
  const latQueueRequest = useApi<LATQueueEntry[]>('messages_v2/lat/queues');

  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);
  const defaultNode =
    state.editingNode !== null &&
    editingNodeData?.type === 'live_agent_transfer'
      ? editingNodeData
      : createLATNode();

  const [editorState, editorDispatch] = useEditorState<LATNodeType>({
    defaultNode,
  });

  const {isFieldReadOnly, canFlowUpdate} = useMutableFieldStatus(
    readOnly,
    defaultNode.type,
  );

  const {node} = editorState;

  const setNode = (payload: LATNodeType) => {
    editorDispatch({type: 'update', payload});
  };

  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const edgeId = Number(state.insertingNode?.edgeId || state.editingNode);
  const editableGraph = createEditableGraph(graph);

  const nodeVariables = findAncestorNodeVariables(
    editableGraph,
    defaultNode,
    edgeId,
    false,
    false,
    allowMeetingNodeVariables,
  );

  const mappedNodeVariables = nodeVariables.map((nodeVar) => ({
    ...nodeVar,
    key: nodeVar.node_id,
    label: nodeVar.name,
  }));

  const hasAncestorJobMatchQuestionNode = getHasAncestorJobMatchQuestionNode(
    editableGraph,
    defaultNode,
    state.insertingNode?.edgeId,
  );

  const dynamicLabels = useChatbotVariables(graph.anchor_entity_type, {
    showDiscoverAttributes: hasAncestorJobMatchQuestionNode,
    showExternalVariables: Boolean(graph.flow_config.api_bot_enabled),
  });

  const prepareContentOnLoad = (
    rdcs: RawDraftContentState,
    decorator: DraftDecoratorType,
  ) =>
    upgradePossibleEntitiesInRDCS(
      rdcs,
      editableGraph,
      nodeVariables,
      dynamicLabels,
      decorator,
    );

  // if node is readonly, we short circuit to false
  // to prevent save
  const isValidNode = !readOnly && validateNode(node);

  const handleSaveNode = () => {
    if (!isValidNode) {
      setHasSubmitted(true);
    } else {
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            payload: node,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload: node,
          });
    }
  };

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  const [currentTab, setCurrentTab] = React.useState({
    label: 'Routing Rules',
    tabId: 'routing',
  });

  const hasRoutingRules =
    node.additional_configs_json.queue_routing_rules.rules.length > 0;

  const [isEditingRules, setIsEditingRules] = React.useState(false);

  const [editingNodeRuleIndex, setEditingNodeRuleIndex] = React.useState<
    number | null | undefined
  >(null);

  const availableVariables = useAllVariables(
    nodeVariables,
    graph.anchor_entity_type,
    Boolean(graph.flow_config.api_bot_enabled),
  );

  if (latQueueRequest.isLoading || latQueueRequest.result == null) {
    return null;
  }

  const latQueues = latQueueRequest.result.map((queue) => ({
    key: queue.id,
    label: queue.name,
  }));

  const selectedOption = latQueues.find(
    (opt) =>
      opt.key ===
      node.additional_configs_json.queue_routing_rules.default_queue_id,
  );

  const updateRule = (ruleIndex: number, updatedRule: QueueRoutingRule) => {
    const nextNode = produce(node, (draft) => {
      draft.additional_configs_json.queue_routing_rules.rules[ruleIndex] =
        updatedRule;
    });
    setNode(nextNode);
  };

  const currentEditingRule =
    editingNodeRuleIndex != null
      ? node.additional_configs_json.queue_routing_rules.rules[
          editingNodeRuleIndex
        ]
      : null;

  const isConnMsgUsingDiscoverAttr = getIsUsingDiscoverAttribute(
    node.additional_configs_json.response_templates.agent_joined,
  );
  const isAwayMsgUsingDiscoverAttr = getIsUsingDiscoverAttribute(
    node.additional_configs_json.response_templates.after_hours,
  );
  const DISCOVER_ATTRIBUTE_WARNING_MESSAGE =
    getDiscoverAttributeWarningMessage(t);

  return (
    <div className={css.latNodeEditorContainer}>
      <div className={css.section}>
        <Input
          label={t('Node_Title', 'Node Title')}
          value={node.name}
          onChange={(event) =>
            setNode({...node, name: event.currentTarget.value})
          }
        />
      </div>
      <div className={css.section}>
        <TabList
          // @ts-expect-error - TS2769 - No overload matches this call.
          onSelect={setCurrentTab}
          selectedTab={currentTab}
          size="medium"
        >
          <Tab label={t('Routing_Rules', 'Routing Rules')} tabId="routing" />
          <Tab
            label={t('Chatbot_Messages', 'Chatbot Messages')}
            tabId="messages"
          />
        </TabList>
      </div>
      {currentTab.tabId === 'routing' && (
        <div className={css.expandoSection}>
          <p>
            {t(
              'The_rules_added_below_defines_how_Chatbot_will_route_users_to_your_internal_team_on_Messaging_To_edit_the_queues_available_go_to',
              'The rules added below defines how Chatbot will route users to your internal team on Messaging. To edit the queues available, go to',
            )}{' '}
            <Link to="/settings/live-agent-transfer">
              {t('Live_Chat_Settings', 'Live Chat Settings')}
            </Link>
            .
          </p>

          <SubTitleLarge className={nodeCss.queueHeader}>
            {t('Default_Queue', 'Default Queue')}
          </SubTitleLarge>

          {latQueues.length === 0 && (
            <p className={css.warningParagraph}>
              <Icon
                className={css.warningIcon}
                color="warning"
                name="warning"
                size="small"
                type="solid"
              />
              {`${t(
                'You_dont_have_any_queues_set_up_Create_new_queues_here',
                'You don’t have any queues set up. Create new queues here',
              )}:`}{' '}
              <Link to="/settings/live-agent-transfer">
                {t('Live_Chat_Settings', 'Live Chat Settings')}
              </Link>
              .
            </p>
          )}

          <Card>
            <Dropdown
              label={t('Select_Queue', 'Select Queue')}
              placeholder={t('Select_Options', 'Select Options')}
              menu={{options: latQueues, allowSearch: true}}
              disabled={readOnly || latQueues.length === 0}
              onChange={(opt) => {
                const next = produce(node, (draft) => {
                  draft.additional_configs_json.queue_routing_rules.default_queue_id =
                    // @ts-expect-error - TS2339 - Property 'key' does not exist on type 'ChangeEvent<HTMLInputElement> | MenuOption'.
                    opt.key;
                });
                setNode(next);
              }}
              dropdownInputText={selectedOption?.label}
            />
          </Card>

          <div className={nodeCss.addRoutingButton}>
            <GenesisButton
              iconLeftName="arrows-split-up-and-left"
              type="secondary"
              disabled={readOnly}
              onClick={() => {
                const nodeWithNewRule = produce(node, (draft) => {
                  draft.additional_configs_json.queue_routing_rules.rules.push({
                    queue_id: '',
                    predicates: [],
                  });
                });
                setNode(nodeWithNewRule);
                setEditingNodeRuleIndex(
                  nodeWithNewRule.additional_configs_json.queue_routing_rules
                    .rules.length - 1,
                );
                setIsEditingRules((prev) => !prev);
              }}
            >
              {t('Add_routing_rules', 'Add routing rules')}
            </GenesisButton>
          </div>

          {hasRoutingRules && (
            <p className={nodeCss.routingNote}>{`${t(
              'Routing_rules_will_be_checked_in_order',
              'Routing rules will be checked in order',
            )}.`}</p>
          )}

          {node.additional_configs_json.queue_routing_rules.rules.map(
            (rule, idx) => {
              return (
                <StaticRoutingCard
                  key={idx}
                  readOnly={readOnly}
                  title={`${t('Routing_Rule', 'Routing Rule')} ${idx + 1}`}
                  rule={rule}
                  variables={availableVariables}
                  availableQueues={latQueues}
                  onSetQueue={(queueId: string) => {
                    const nextNode = produce(node, (draft) => {
                      draft.additional_configs_json.queue_routing_rules.rules[
                        idx
                      ].queue_id = queueId;
                    });
                    setNode(nextNode);
                  }}
                  onDelete={() => {
                    const nextNode = produce(node, (draft) => {
                      draft.additional_configs_json.queue_routing_rules.rules.splice(
                        idx,
                        1,
                      );
                    });
                    setNode(nextNode);
                  }}
                  onEdit={() => {
                    setEditingNodeRuleIndex(idx);
                    setIsEditingRules(true);
                  }}
                />
              );
            },
          )}

          {hasRoutingRules ? <div /> : <div></div>}
        </div>
      )}
      {currentTab.tabId === 'messages' && (
        <div className={css.section}>
          <p>{`${t(
            'Customize_messages_for_various_steps_of_the_Live_Chat',
            'Customize messages for various steps of the Live Chat',
          )}:`}</p>

          <SubTitleSmall className={nodeCss.draftEditorHeader}>
            {`${t(
              'When_connecting_to_an_agent_display_this_message',
              'When connecting to an agent, display this message',
            )}:`}
          </SubTitleSmall>
          {richTextEditorEnabled ? (
            <RichEditorWithWarning
              warningMessage={
                isConnMsgUsingDiscoverAttr
                  ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                  : ''
              }
              placeholder={`${t(
                'Please_hold_on_we_are_connecting_you_now',
                'Please hold on, we are connecting you now',
              )}.`}
              defaultValue={
                node.additional_configs_json.response_templates.agent_joined
              }
              onChange={(val) => {
                const next = produce(node, (draft) => {
                  draft.additional_configs_json.response_templates[
                    'agent_joined'
                  ] = val;
                });
                setNode(next);
              }}
              editorHeight="160px"
              includeFormatting={Boolean(richTextEditorFormattingEnabled)}
              includeHyperlink={!chatbotNewHyperlinkModalEnabled}
              includeHyperlinkV2={Boolean(chatbotNewHyperlinkModalEnabled)}
              includeGif
              // @ts-expect-error - TS2322 - Type '{ key: number; label: string; name: string; node_id: number; node_name: string; derived_field_name?: string | undefined; template: string; node_type?: string | undefined; attribute_name?: string | undefined; }[]' is not assignable to type 'MenuOption[]'.
              nodeVariables={mappedNodeVariables}
              atsVariables={
                graph.use_case === 'sourcing' ? undefined : dynamicLabels
              }
              readOnly={isFieldReadOnly(
                'additional_configs_json.response_templates.agent_joined',
              )}
              entityType={graph.anchor_entity_type}
              prepareContentOnLoad={prepareContentOnLoad}
              atsTooltip={
                hasAncestorJobMatchQuestionNode
                  ? 'ATS & External variables'
                  : 'ATS variables'
              }
              enableDraftJSONCopy
            />
          ) : (
            <MessageDraftEditor
              placeholder={`${t(
                'Please_hold_on_we_are_connecting_you_now',
                'Please hold on, we are connecting you now',
              )}.`}
              defaultValue={
                node.additional_configs_json.response_templates.agent_joined
              }
              onChange={(val) => {
                const next = produce(node, (draft) => {
                  draft.additional_configs_json.response_templates[
                    'agent_joined'
                  ] = val;
                });
                setNode(next);
              }}
              entityType={graph.anchor_entity_type}
              isFirstSmsMessage={false}
              label=""
              extraCharacterCount={0}
              useCase={graph.use_case}
              nodeVariables={nodeVariables}
              readOnly={isFieldReadOnly(
                'additional_configs_json.response_templates.agent_joined',
              )}
              showDiscoverAttributes={hasAncestorJobMatchQuestionNode}
              showExternalVariables={Boolean(graph.flow_config.api_bot_enabled)}
              warningMessage={
                isConnMsgUsingDiscoverAttr
                  ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                  : ''
              }
            />
          )}

          <SubTitleSmall className={nodeCss.draftEditorHeader}>
            {`${t(
              'If_outside_of_business_hours_display_this_message',
              'If outside of business hours, display this message',
            )}:`}
          </SubTitleSmall>
          {richTextEditorEnabled ? (
            <RichEditorWithWarning
              warningMessage={
                isAwayMsgUsingDiscoverAttr
                  ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                  : ''
              }
              placeholder={`${t(
                'Sorry_this_request_is_outside_business_hours_Try_again_later',
                'Sorry, this request is outside business hours. Try again later',
              )}.`}
              defaultValue={
                node.additional_configs_json.response_templates.after_hours
              }
              onChange={(val) => {
                const next = produce(node, (draft) => {
                  draft.additional_configs_json.response_templates[
                    'after_hours'
                  ] = val;
                });
                setNode(next);
              }}
              editorHeight="160px"
              includeFormatting={Boolean(richTextEditorFormattingEnabled)}
              includeHyperlink={!chatbotNewHyperlinkModalEnabled}
              includeHyperlinkV2={Boolean(chatbotNewHyperlinkModalEnabled)}
              includeGif
              // @ts-expect-error - TS2322 - Type '{ key: number; label: string; name: string; node_id: number; node_name: string; derived_field_name?: string | undefined; template: string; node_type?: string | undefined; attribute_name?: string | undefined; }[]' is not assignable to type 'MenuOption[]'.
              nodeVariables={mappedNodeVariables}
              atsVariables={
                graph.use_case === 'sourcing' ? undefined : dynamicLabels
              }
              readOnly={isFieldReadOnly(
                'additional_configs_json.response_templates.after_hours',
              )}
              entityType={graph.anchor_entity_type}
              prepareContentOnLoad={prepareContentOnLoad}
              atsTooltip={
                hasAncestorJobMatchQuestionNode
                  ? t('ATS_External_variables', 'ATS & External variables')
                  : t('ATS_variables', 'ATS variables')
              }
              enableDraftJSONCopy
            />
          ) : (
            <MessageDraftEditor
              placeholder={`${t(
                'Sorry_this_request_is_outside_business_hours_Try_again_later',
                'Sorry, this request is outside business hours. Try again later',
              )}.`}
              defaultValue={
                node.additional_configs_json.response_templates.after_hours
              }
              onChange={(val) => {
                const next = produce(node, (draft) => {
                  draft.additional_configs_json.response_templates[
                    'after_hours'
                  ] = val;
                });
                setNode(next);
              }}
              entityType={graph.anchor_entity_type}
              isFirstSmsMessage={false}
              label=""
              extraCharacterCount={0}
              useCase={graph.use_case}
              nodeVariables={nodeVariables}
              readOnly={isFieldReadOnly(
                'additional_configs_json.response_templates.after_hours',
              )}
              showDiscoverAttributes={hasAncestorJobMatchQuestionNode}
              showExternalVariables={Boolean(graph.flow_config.api_bot_enabled)}
              warningMessage={
                isAwayMsgUsingDiscoverAttr
                  ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                  : ''
              }
            />
          )}
        </div>
      )}
      <div className={css.dumbFooter}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={hasSubmitted ? true : !isValidNode}
        >
          {canFlowUpdate ? t('Update', 'Update') : t('Save', 'Save')}
        </Button>
      </div>
      {isEditingRules && (
        <ModalScrim onClick={() => setIsEditingRules(false)} />
      )}
      {isEditingRules &&
        editingNodeRuleIndex != null &&
        currentEditingRule != null && (
          <RuleEditor
            variables={availableVariables}
            rule={currentEditingRule}
            onAddPredicates={(predicates) => {
              if (editingNodeRuleIndex != null && currentEditingRule != null) {
                const nextRule = produce(currentEditingRule, (draft) => {
                  draft.predicates = predicates;
                });
                updateRule(editingNodeRuleIndex, nextRule);
                setIsEditingRules(false);
                setEditingNodeRuleIndex(null);
              }
            }}
          />
        )}
    </div>
  );
}

function StaticRoutingCard({
  // @ts-expect-error - TS7031 - Binding element 'rule' implicitly has an 'any' type.
  rule,
  // @ts-expect-error - TS7031 - Binding element 'readOnly' implicitly has an 'any' type.
  readOnly,
  // @ts-expect-error - TS7031 - Binding element 'onDelete' implicitly has an 'any' type.
  onDelete,
  // @ts-expect-error - TS7031 - Binding element 'onEdit' implicitly has an 'any' type.
  onEdit,
  // @ts-expect-error - TS7031 - Binding element 'onSetQueue' implicitly has an 'any' type.
  onSetQueue,
  // @ts-expect-error - TS7031 - Binding element 'title' implicitly has an 'any' type.
  title,
  // @ts-expect-error - TS7031 - Binding element 'availableQueues' implicitly has an 'any' type.
  availableQueues,
  // @ts-expect-error - TS7031 - Binding element 'variables' implicitly has an 'any' type.
  variables,
}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const selectedOption = availableQueues.find(
    // @ts-expect-error - TS7006 - Parameter 'opt' implicitly has an 'any' type.
    (opt) => opt.key === rule.queue_id,
  );

  return (
    <div className={nodeCss.staticRoutingCard}>
      <div className={nodeCss.staticRoutingHeader}>
        <SubTitleMedium>{title}</SubTitleMedium>
        <div className={nodeCss.staticRoutingActions}>
          <GenesisButton
            onClick={onDelete}
            disabled={readOnly}
            size="small"
            iconRightName="trash-can"
            type="tertiary"
          />
          <GenesisButton
            onClick={onEdit}
            disabled={readOnly}
            size="small"
            iconRightName="pen"
            type="tertiary"
          />
        </div>
      </div>
      <Card>
        <div className={nodeCss.staticCardContents}>
          <div className={nodeCss.staticCardRules}>
            {/* @ts-expect-error - TS7006 - Parameter 'pred' implicitly has an 'any' type. | TS7006 - Parameter 'idx' implicitly has an 'any' type. */}
            {rule.predicates.map((pred, idx) => {
              const variable = variables.find(
                // @ts-expect-error - TS7006 - Parameter 'v' implicitly has an 'any' type.
                (v) => v.__key === keyForPredicate(pred),
              );

              if (!variable) {
                return null;
              }
              return (
                <p key={idx}>
                  <span>{idx === 0 ? t('IF', 'IF') : t('and', 'and')}</span>{' '}
                  <strong>{variable?.__label}</strong>{' '}
                  {PRED_TYPE_LABEL_MAP(t)[pred.pred_type]}{' '}
                  {Array.isArray(pred.value) ? (
                    // @ts-expect-error - TS7006 - Parameter 'val' implicitly has an 'any' type. | TS7006 - Parameter 'idx2' implicitly has an 'any' type.
                    pred.value.map((val, idx2) => (
                      <Chip
                        key={idx2}
                        classNames={{wrapper: nodeCss.staticChip}}
                      >
                        {val}
                      </Chip>
                    ))
                  ) : (
                    <Chip classNames={{wrapper: nodeCss.staticChip}}>
                      {pred.value}
                    </Chip>
                  )}
                </p>
              );
            })}
          </div>
          <div className={nodeCss.queueSelectRow}>
            <div>
              <p>{t('Route_to_this_queue', 'Route to this queue')}</p>
            </div>
            <Dropdown
              placeholder={t('Select_Queue', 'Select Queue')}
              menu={{
                options: availableQueues,
                selectedKeys: selectedOption
                  ? [selectedOption?.key]
                  : undefined,
              }}
              dropdownInputText={selectedOption?.label ?? undefined}
              disabled={readOnly || availableQueues.length === 0}
              // @ts-expect-error - TS2339 - Property 'key' does not exist on type 'ChangeEvent<HTMLInputElement> | MenuOption'.
              onChange={(opt) => onSetQueue(opt.key)}
              // @ts-expect-error - TS17001 - JSX elements cannot have multiple attributes with the same name.
              dropdownInputText={selectedOption?.label}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
// @ts-expect-error - TS7031 - Binding element 'onClick' implicitly has an 'any' type.
function ModalScrim({onClick}) {
  return <div className={nodeCss.scrim} onClick={onClick} />;
}
